// Utilitaire pour gérer les rejets de promesses non capturés

interface PromiseRejectionHandler {
  onError?: (error: any) => void;
  fallbackMessage?: string;
}

export const setupPromiseRejectionHandler = (options: PromiseRejectionHandler = {}) => {
  const { onError, fallbackMessage = 'Une erreur inattendue est survenue' } = options;

  // Gérer les rejets de promesses non capturés
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);

    // Éviter l'affichage par défaut du navigateur
    event.preventDefault();

    // Gérer les erreurs spécifiques
    if (event.reason?.error?.includes('Cash register not found')) {
      console.warn('Cash register session issue detected');
      return;
    }

    // Appeler le handler personnalisé si fourni
    if (onError) {
      onError(event.reason);
    }

    // Logger l'erreur pour debugging
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Unhandled Promise Rejection');
      console.error('Reason:', event.reason);
      console.error('Stack:', event.reason?.stack);
      console.groupEnd();
    }
  });

  // Gérer les erreurs non capturées (synchrone)
  window.addEventListener('error', (event) => {
    console.error('Unhandled error:', event.error);

    if (onError) {
      onError(event.error);
    }

    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Unhandled Error');
      console.error('Message:', event.message);
      console.error('Filename:', event.filename);
      console.error('Line:', event.lineno, 'Column:', event.colno);
      console.error('Error:', event.error);
      console.groupEnd();
    }
  });
};

// Fonction utilitaire pour wrapper les promesses avec gestion d'erreur
export const safePromise = <T>(
  promise: Promise<T>,
  errorHandler?: (error: any) => void
): Promise<T | null> => {
  return promise
    .then((result) => result)
    .catch((error) => {
      console.error('Promise error handled safely:', error);
      if (errorHandler) {
        errorHandler(error);
      }
      return null;
    });
};

// Fonction pour créer des promesses avec timeout
export const promiseWithTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutError?: Error
): Promise<T> => {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(timeoutError || new Error(`Promise timeout after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]);
};
